import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, Row, Col, Form, CloseButton } from 'react-bootstrap';
import createAxiosInstance from '../../services/AxiosConfig';
import styles from "./Appointment.module.css";
import { UserProfilePictureIcon, HangupIcon, ExpandIcon } from "../../components/icons/Icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWebSocket } from '../../services/webSocket/useWebSocket';
import { removeMeeting, leaveMeeting, resetLeaveMeetingFlag, toggleMinimized } from '../appointment/AppointmentSlicer';

import {
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  ConsoleLogger,
  DefaultDeviceController,
} from 'amazon-chime-sdk-js';
import { showAlert } from '../../components/PerinHealthAlertSlice';

function AppointmentVideoCall({audioOnly = false, onClose, data }) {
  const meetingSessionRef = useRef(null);
  const meetingIdRef = useRef(null);
  const meetingArnRef = useRef(null);
  const audioElementRef = useRef(null);
  const previewVideoRef = useRef(null);
  const [startTime, setStartTime] = useState(null);
  const [meetingData, setMeetingData] = useState(null);
  const [videoTiles, setVideoTiles] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [captureMedia, setCaptureMedia] = useState(true);
  const [addInsurance, setAddInsurance] = useState(false);
  const [shareScreen, setShareScreen] = useState(false);
  const [hideVideo, setHideVideo] = useState(false);
  const [inPreviewMode, setInPreviewMode] = useState(true);
  const [transcriptions, setTranscriptions] = useState([]);
  const [showTranscription, setShowTranscription] = useState(false);
  const axiosConfig = useSelector(state => state.axiosConfig);
  const userRole = useSelector((state) => state.userInfo.roleCategory);
  const userId = useSelector((state) => state.userInfo.id);
  const dispatch = useDispatch();
  const localAttendeeIdRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  const {leaveMeetingData, minimized} = useSelector(state => state.appointment);
  const [isVideoPreviewReady, setIsVideoPreviewReady] = useState(false);
  const meetingEndedRef = useRef(false);
  const deviceControllerRef = useRef(null);

  const {sendMessage} = useWebSocket();
  const httpService = createAxiosInstance(axiosConfig.envURL, dispatch);

  const currentMeetings = useSelector((state) => state.appointment.currentMeetings).flat();

  const toggleMute = useCallback(async () => {
    if (!meetingSessionRef.current?.audioVideo) return;
    if (isMuted) {
      await meetingSessionRef.current.audioVideo.realtimeUnmuteLocalAudio();
    } else {
      await meetingSessionRef.current.audioVideo.realtimeMuteLocalAudio();
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  const toggleCaptureMedia = () => {
    setCaptureMedia(!captureMedia);
  }

  const toggleVideo = useCallback(async () => {
    if (!meetingSessionRef.current) return;
    const audioVideo = meetingSessionRef.current.audioVideo;
    if (!audioVideo) return;
  
    if (!hideVideo) {
      audioVideo.stopLocalVideoTile(); 
      setHideVideo(true);
    } else {
        audioVideo.startLocalVideoTile(); 
        setHideVideo(false);
      }
      
    
  }, [hideVideo]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  useEffect(() => {
    let timer;
    if (!inPreviewMode) {
      timer = setInterval(() => {
        setCallDuration(prevDuration => prevDuration + 1);
      }, 1000);
    } else {
      clearInterval(timer);
      setCallDuration(0);
    }
    return () => clearInterval(timer);
  }, [inPreviewMode]);

  const startMediaCapture = useCallback(async () => {
    try {
      await httpService.post(`/meeting/mediacapture/start`, { 
        meetingArn: meetingArnRef.current,
        meetingId: meetingIdRef.current,
        patientId: data.patientId,
      });

    } catch (err) {
      console.error('Failed to start media capture:', err);
      throw err;
    }
  }, [httpService, data]);

  const joinMeeting = useCallback(async () => {
    if (!meetingData || !meetingSessionRef.current?.audioVideo) return;
    const audioVideo = meetingSessionRef.current.audioVideo;
    if (!audioOnly && previewVideoRef.current) {
      audioVideo.stopVideoPreviewForVideoInput(previewVideoRef.current);
    }
    
    // Set audio output device now that we have a user gesture (user clicked "Start Call")
    try {
      const audioOutputDevices = await audioVideo.listAudioOutputDevices();
      if (audioOutputDevices.length > 0) {
        await audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
      }
    } catch (sinkErr) {
      // Silently handle NotAllowedError - browser will use default audio output
      if (sinkErr?.name !== 'NotAllowedError') {
        console.warn('[Chime] chooseAudioOutput failed (non-critical):', sinkErr?.message || sinkErr);
      }
    }
    
    audioVideo.start();
    if (!audioOnly) {
      audioVideo.startLocalVideoTile();
    }
    sendMessage({
      type: 'doctorHasJoined',
      meetingId: meetingIdRef,
      physicianId: data.physicianId,
      patientId: data.patientId,
      appointmentId: data.appointmentId
    })
    setInPreviewMode(false);
    
    setStartTime(new Date().toISOString());
    if (captureMedia) {
      dispatch(showAlert({
          header: "Transcription Started",
          message: "Appointment transcription has started recording.",
          type: 'info'
      }));
    }
   
  }, [meetingData, audioOnly, data, sendMessage, dispatch, captureMedia]);

  const toggleTranscription = useCallback( async() => {
    if (showTranscription) {
      // Stop transcription
      httpService.post(`/meeting/transcription/stop`, { meetingId: meetingIdRef.current })
        .then(response => {
          setShowTranscription(false);
        })
        .catch(err => console.error('Failed to stop transcription:', err));
    } else {
      // Start transcription
      httpService.post(`/meeting/transcription/start`, { meetingId: meetingIdRef.current })
        .then(response => {
          setShowTranscription(true);
        })
        .catch(err => console.error('Failed to start transcription:', err));
    }
  }, [showTranscription, httpService]);

  useEffect(() => {
    if (!inPreviewMode && captureMedia) {
      startMediaCapture();
    }
  }, [inPreviewMode, startMediaCapture]);
  
  const leaveMeeting = async () => {
    meetingEndedRef.current = true;
    if (meetingSessionRef.current) {
      const audioVideo = meetingSessionRef.current.audioVideo;
      if (audioVideo) {
        // 1. Stop video preview first (required before releasing video input)
        if (!audioOnly && previewVideoRef.current) {
          try {
            audioVideo.stopVideoPreviewForVideoInput(previewVideoRef.current);
          } catch (e) {
          }
        }
        await audioVideo.stopLocalVideoTile();
        await audioVideo.stopVideoInput();
        await audioVideo.stopAudioInput();
        await audioVideo.stop();
        audioVideo.unbindAudioElement();
      }
      // 2. Destroy device controller to release all media streams (camera/mic)
      if (deviceControllerRef.current) {
        try {
          await deviceControllerRef.current.destroy();
        } catch (e) {
        }
        deviceControllerRef.current = null;
      }
      if (meetingIdRef.current) {
        try {
          await httpService.delete(`/meeting/${meetingIdRef.current}`);
          // Clean up any other resources or perform additional state cleanup here
        } catch (err) {
          console.error('Failed to delete meeting:', err);
        }
      }
    }
   
    onClose();
    dispatch(removeMeeting(data.appointmentId));
    sendMessage({
      type: 'doctorHasLeft',
      meetingId: meetingIdRef,
      physicianId: data.physicianId,
      patientId: data.patientId,
    });
    if(!inPreviewMode){
      updateMeetingMinutes();      
    }else{
      setMeetingInactive();
    }
    // Reset any state or refs that might persist
    setMeetingData(null);
    meetingSessionRef.current = null;
    if (captureMedia && !inPreviewMode) {
      dispatch(
        showAlert({
            header: "Appointment ended",
            message: "Transcription is processing and will be available under appointment summary shortly.",
            type: 'success',
        })
      );
    }

  };

  const setMeetingInactive = async() => {
    const formData = {
      isActive: false
    };

    httpService.put(`/appointment/${data.appointmentId}`, formData)
        .then(response => {
        })
        .catch(err => console.error('Failed to update appointment:', err));
  }



  const updateMeetingMinutes = async() => {
    const formData = {
      duration: callDuration,
      actualAppointmentStartTime: startTime || new Date().toISOString(),
      actualAppointmentEndTime: new Date().toISOString(),
      meetingId: meetingIdRef.current,
      isActive: false
  };
  
    httpService.put(`/appointment/${data.appointmentId}`, formData)
        .then(response => {
        })
        .catch(err => console.error('Failed to update appointment:', err));
  }

  useEffect(() => {
    if (leaveMeetingData?.shouldLeave) {
        if (leaveMeetingData.meetingId === meetingIdRef.current && leaveMeetingData.patientId === data.patientId) {
            leaveMeeting();
            dispatch(resetLeaveMeetingFlag());
        }
    }
}, [leaveMeetingData, dispatch, leaveMeeting]);

  useEffect(() =>  {
    const startMeetingSetup = async () => {

      const currentMeeting = currentMeetings.find(
        meeting => meeting.appointmentId === data.appointmentId
      );
      try {
          const postData = {
            title: data.title,
            appointmentId: data.appointmentId,
            senderId: data.physicianId,
            recipientId: data.patientId,
            senderName: data.accountName,
            audioOnly: false
        };

          // Only add meetingId if it exists
        if (currentMeeting?.meeting?.MeetingId) {
            postData.meetingId = currentMeeting.meeting.MeetingId;
        }else if (data.meetingId){
            postData.meetingId = data.meetingId;
        }

        const meetingResponse = await httpService.request({
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          url: `/meeting/join`,
          data: JSON.stringify(postData),
        });

        if (meetingResponse.data.data.meeting && meetingResponse.data.data.attendee) {
          const { meeting, attendee } = meetingResponse.data.data;
          meetingIdRef.current = meeting.MeetingId;
          meetingArnRef.current = meeting.MeetingArn;
          setMeetingData({
            Meeting: meeting,
            Attendee: attendee,
          });
        }
      } catch (err) {
        console.error('Error setting up the meeting:', err);
      }
    };

    startMeetingSetup();
  }, [userId, data, userRole, httpService]);

  useEffect(() => {
    if (meetingData) {
      localAttendeeIdRef.current = meetingData.Attendee.AttendeeId;
    }
  }, [meetingData]); 

  useEffect(() => {
    if (!meetingData) return;

    const logger = new ConsoleLogger('MeetingLogs', LogLevel.INFO);
    const deviceController = new DefaultDeviceController(logger);
    deviceControllerRef.current = deviceController;
    const configuration = new MeetingSessionConfiguration(meetingData.Meeting, meetingData.Attendee);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

    meetingSessionRef.current = meetingSession;

    if (meetingSessionRef.current) {
      meetingSessionRef.current.audioVideo.transcriptionController.subscribeToTranscriptEvent((transcriptEvent) => {
        const results = transcriptEvent.results || []; 
        setTranscriptions((currentTranscriptions) => {

          if (results.length === 0) {
            return currentTranscriptions;
          }
      
          return results.reduce((updatedTranscriptions, result) => {
            const index = updatedTranscriptions.findIndex(t => t.resultId === result.resultId);
            const items = result.alternatives[0]?.items || [];
      
            const transcriptionText = items.map(item => item.content).join(' ');
      
            const transcriptionData = {
              resultId: result.resultId,
              text: transcriptionText,
              isFinal: !result.isPartial,
              speaker: items[0]?.attendee?.externalUserId 
                        ? items[0].attendee.externalUserId.split('_')[1] 
                        : "Unknown",
              startTime: items[0]?.startTimeMs || 0,
              endTime: items[0]?.endTimeMs || 0,
            };
      
            if (index === -1) {
              // New transcription result
              return [...updatedTranscriptions, transcriptionData];
            } else {
              // Update existing transcription result if partial
              if (result.isPartial) {
                updatedTranscriptions[index] = transcriptionData;
              }
              return updatedTranscriptions;
            }
          }, currentTranscriptions);
        });
      });      
    }

    const observer = {
      audioVideoDidStart: () => {
        meetingEndedRef.current = false;
      },
      audioVideoDidStop: () => {
        meetingEndedRef.current = true;
      },
      videoTileDidUpdate: tileState => {
        if (audioOnly) return;
        
        const isLocalTile = tileState.localTile;
        const boundAttendeeId = tileState.boundAttendeeId || (isLocalTile ? localAttendeeIdRef.current : null);
        
        if (!boundAttendeeId && !isLocalTile) return;

        setVideoTiles(prevTiles => {
          const tileIndex = prevTiles.findIndex(t => t.tileId === tileState.tileId);
          if (tileIndex !== -1) {
            const existingTile = prevTiles[tileIndex];
            if (isLocalTile && existingTile.ref?.current && meetingSessionRef.current?.audioVideo) {
              const audioVideo = meetingSessionRef.current.audioVideo;
              try {
                const tile = audioVideo.getVideoTile(tileState.tileId);
                if (tile) {
                  audioVideo.bindVideoElement(tileState.tileId, existingTile.ref.current);
                } else if (!tileState.boundAttendeeId || tileState.boundAttendeeId === '') {
                  if (!meetingEndedRef.current) {
                    if (!audioOnly) {
                      audioVideo.startLocalVideoTile();
                    }
                  }
                }
              } catch (err) {
                console.error('[Chime] Error rebinding local tile:', err);
              }
            }
            return prevTiles;
          }

          const newRef = React.createRef();
          return [...prevTiles, { tileId: tileState.tileId, ref: newRef, boundAttendeeId, localTile: isLocalTile }];
        });
      },
      videoTileWasRemoved: tileId => {
        if (audioOnly) return;
        setVideoTiles(prevTiles => {
          const tileToRemove = prevTiles.find(t => t.tileId === tileId);
          if (tileToRemove?.localTile) {
            return prevTiles;
          }
          return prevTiles.filter(tile => tile.tileId !== tileId);
        });
      },
    };

    const controllerObserver = {
      eventDidReceive(name, attributes) {
        if (name === 'meetingEnded') {
          meetingEndedRef.current = true;
          // Don't call leaveMeeting() automatically - let the user or backend handle it
          // This prevents automatic cleanup that might interfere with the UI
        }
      }
    };

    meetingSession.audioVideo.addObserver(observer);
    meetingSession.eventController.addObserver(controllerObserver);
    
    const setupDevices = async () => {
      try {
        meetingSession.audioVideo.setDeviceLabelTrigger(async () => {
          const constraints = {
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            video: !audioOnly
          };
          return await navigator.mediaDevices.getUserMedia(constraints);
        });

        const audioInputDevices = await deviceController.listAudioInputDevices();
        if (audioInputDevices.length === 0) {
          console.error('[Chime] No audio input devices found');
          return;
        }
        await deviceController.startAudioInput(audioInputDevices[0].deviceId);
        if (audioElementRef.current) {
          meetingSession.audioVideo.bindAudioElement(audioElementRef.current);
        }

        if (!audioOnly) {
          const videoInputDevices = await deviceController.listVideoInputDevices();
          if (videoInputDevices.length > 0 && previewVideoRef.current) {
            await deviceController.startVideoInput(videoInputDevices[0].deviceId);
            await meetingSession.audioVideo.startVideoPreviewForVideoInput(previewVideoRef.current);
            setIsVideoPreviewReady(true);
          } else if (videoInputDevices.length === 0) {
          }
        } else {
          setIsVideoPreviewReady(true);
        }

      } catch (err) {
        console.error('[Chime] setupDevices failed:', err);
      }
    };

    const runSetup = () => {
      if (audioOnly || previewVideoRef.current) {
        setupDevices();
      } else {
        requestAnimationFrame(() => {
          if (previewVideoRef.current) setupDevices();
        });
      }
    };
    runSetup();

    return () => {
      meetingSession.audioVideo.transcriptionController.unsubscribeFromTranscriptEvent()
      meetingSession.audioVideo.removeObserver(observer);
      meetingSession.eventController.removeObserver(controllerObserver);
      if (deviceControllerRef.current) {
        deviceControllerRef.current.destroy().catch(() => {});
        deviceControllerRef.current = null;
      }
    };
  }, [meetingData, audioOnly]);

  useEffect(() => {
    if (!meetingSessionRef.current?.audioVideo) return;
    // Don't attempt recovery if meeting has ended
    if (meetingEndedRef.current) {
      return;
    }
    videoTiles.forEach(tile => {
      if (tile.ref?.current) {
        const audioVideo = meetingSessionRef.current.audioVideo;
        try {
          const chimeTile = audioVideo.getVideoTile(tile.tileId);
          if (chimeTile) {
            audioVideo.bindVideoElement(tile.tileId, tile.ref.current);
          } else {
            if (tile.localTile && !audioOnly && !meetingEndedRef.current) {
              audioVideo.startLocalVideoTile();
            }
          }
        } catch (err) {
          console.error(`[Chime] Error binding video element for tile ${tile.tileId}:`, err);
        }
      }
    });
  }, [videoTiles, audioOnly]);
  
  const isSolo = videoTiles.length === 1;
  
  return (
    <>
      <div className={minimized ? styles.AppointmentCallContainerMinimized : styles.AppointmentCallContainer}>
        <div className={styles.videoHeader}>
          <Row> 
              <Col xs={3}></Col>
              <Col xs={6} className={styles.VideoHeaderText}>
                {!minimized && 
                <>
                  <div className={styles.iconContainer}>
                    <span className={styles.iconText}>{data.patientName?.split(' ')[0][0] + data.patientName?.split(' ')[1][0]}</span>
                    <UserProfilePictureIcon width="38" height="38" />
                  </div>
                  {`${data.patientName} Appointment`}
                </>
                }
              </Col>
              <Col xs={3} className="d-flex align-items-center justify-content-end">
                <Button variant="white" style={{padding:"0"}} onClick={()=>dispatch(toggleMinimized())}>
                  <ExpandIcon />
                </Button>
                <CloseButton onClick={leaveMeeting} style={{paddingRight:"30px"}}/>
              </Col>
          </Row>
        </div>
        <div className={styles.videoMiddle}>
          {/* <Row className={styles.videoRow}> */}
            <Col md={showTranscription ? 9 : 12} className={styles.videoCol}>
            {audioOnly ? (
              <div style={{ width: '100%', height: '450px', background: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'grey', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', color: 'white' }}>
                  {'NB'}
                </div>
              </div>
            ) : (
              inPreviewMode ? (
                <>
                  <div>
                    <p className={styles.videoWelcomeText}>Welcome to Telehealth!</p>
                    <p className={styles.videoEnsure}>Please ensure your camera and microphone are enabled</p>
                  </div>
                  <Row>
                    <Col xs={12} className="d-flex justify-content-center">
                      <video ref={previewVideoRef} autoPlay playsInline className={styles.previewVideo} />
                    </Col>
                  </Row>
                </>
              ) : (
                videoTiles.map(tile => (
                  <>
                <Row>
                    <Col xs={12} className="d-flex justify-content-center">
                    <video
                      style= { (showTranscription && tile.boundAttendeeId === localAttendeeIdRef.current ) ? {} : {}  }
                      key={tile.tileId}
                      ref={tile.ref}
                      autoPlay
                      playsInline
                      className={`${styles.videoElement} ${
                        isSolo ? styles.primaryVideo : (tile.boundAttendeeId === localAttendeeIdRef.current ? (minimized ? styles.localVideoMinimized : styles.localVideo) : styles.remoteVideo)
                      }`}
                    />
                    </Col>
                  </Row>
                </>
                ))
              )
            )}
            </Col>    
            {showTranscription && (
              <Col md={3} style={{ overflowY: 'auto', height: '100%' }}>
                {transcriptions.map((transcription) => (
                  <div key={transcription.resultId}>
                    <strong>{transcription.speaker}</strong>: {transcription.text}
                  </div>
                ))}
              </Col>
            )}   
          {/* </Row> */}
        </div>
        <div>
          <Row className="w-100 justify-center-content"> 
          {(inPreviewMode || (!inPreviewMode && !minimized)) &&
            <Col xs={3} md={3} className="d-flex justify-content-flex-start">
              {!inPreviewMode &&  !minimized &&    
                <div style={{ textAlign: 'left', display:"flex", alignSelf:"center"}}>
                  <span><FontAwesomeIcon icon={['far', 'fa-clock']} /> {formatTime(callDuration)}</span>
                </div>
              }
            </Col>
          }
            <Col xs={6} md={6} className={styles.rowContent}>
                {inPreviewMode ?
                  <Form style={{display:"flex", alignItems:"center"}}>
                    <Form.Check
                        type="switch"
                        id="custom-switch"
                        checked={!isMuted}
                        onChange={toggleMute}
                    />
                    {isMuted ? (
                      <FontAwesomeIcon icon="fa-solid fa-microphone-slash" style={{color: "#4F4F4F", width:"20px" }} size="lg"/>
                    ) : (
                      <FontAwesomeIcon icon="fa-solid fa-microphone" style={{color: "#4F4F4F", width:"20px"}} size="lg" />
                    )}                    
                    <Form.Check
                        type="switch"
                        id="custom-switch"
                        checked={captureMedia}
                        onChange={toggleCaptureMedia}
                        style={{marginLeft: '25px'}}
                    />
                    <FontAwesomeIcon icon="fa-solid fa-notes-medical" style={{color: "#4F4F4F"}} size="lg" />
                    {/* <Form.Check
                        type="switch"
                        id="custom-switch"
                        checked={addInsurance}
                        onChange={()=>setAddInsurance(!addInsurance)}
                        style={{marginLeft: '25px'}}
                    />
                    <span>Allow Patient to update insurance</span> */}
                  </Form>
                  
                  : ( 
                    <>
                      <Button className={styles.AppointmentToggleButtons} onClick={toggleVideo} variant="secondary">
                        {hideVideo ? (
                          <FontAwesomeIcon icon={['fas', 'video-slash']} style={{color:"#4F4F4F"}} size="xl"/>
                        ) : (
                          <FontAwesomeIcon icon={['fas', 'video']} style={{color:"#4F4F4F"}} size="xl"/>
                        )}
                      </Button>
                      <Button className={styles.AppointmentToggleButtons} onClick={toggleMute} variant="secondary">
                        {isMuted ? (
                          <FontAwesomeIcon icon={['fas', 'microphone-slash']} style={{color:"#4F4F4F"}} size="xl"/>
                        ) : (
                          <FontAwesomeIcon icon={['fas', 'microphone']} style={{color:"#4F4F4F"}} size="xl"/>
                        )}
                      </Button>
                      {!minimized &&        
                        <Button className={styles.AppointmentToggleButtons} onClick={toggleTranscription} variant="secondary">
                          {showTranscription ? (
                            <FontAwesomeIcon icon={['fas', 'stop']} style={{color:"#4F4F4F"}} size="xl"/>
                          ) : (
                            <FontAwesomeIcon icon={['fas', 'quote-right']} style={{color:"#4F4F4F"}} size="xl"/>
                          )}
                        </Button>
                      }
                      {!minimized &&        
                        <Button className={styles.AppointmentToggleButtons} variant="secondary">
                          <FontAwesomeIcon icon="fa-solid fa-display" style={{color:shareScreen ? "red" : "#4F4F4F"}} size="xl"/>
                        </Button>
                      }
                      {!minimized &&        
                        <Button className={styles.AppointmentToggleButtons} variant="secondary">
                           <FontAwesomeIcon icon="fa-solid fa-message" style={{color:"#4F4F4F"}} size="xl"/>
                        </Button>
                      }
                    </>  
                  )
                }     
            </Col>
            <Col xs={(!inPreviewMode && minimized) ? 6 : 3} md={(!inPreviewMode && minimized) ? 6 : 3} className={`d-flex align-items-center ${minimized ? "justify-content-center" : "justify-content-end"}`} style={(!inPreviewMode && minimized) ? {gap: "10px"} : {}}>
              {!inPreviewMode &&
                <Button onClick={leaveMeeting} variant="danger" className={styles.endCallButtonAppointments}>
                  <HangupIcon />
                  {!minimized && 'End Call'}
                </Button>
              }
              {!inPreviewMode && minimized &&
                  <div style={{ textAlign: 'left', display:"flex", alignSelf:"center"}}>
                    <span>{formatTime(callDuration)}</span>
                  </div>
              }
            </Col>
          </Row>
          {inPreviewMode && 
            <Row className="w-100 mt-2 justify-center-content">
              <Col xs={6} md={6}>
                <Button 
                  onClick={joinMeeting} 
                  disabled={!isVideoPreviewReady}
                  variant="success" 
                  className={styles.StartCallAppointment}
                  >
                    Start Call
                  </Button>
              </Col>
              <Col xs={6} md={6}>
                <Button onClick={leaveMeeting} variant="danger" className={styles.EndCallAppointment}>End Call</Button>
              </Col>
            </Row>
          }
          
        </div>
      </div>
      <audio ref={audioElementRef} style={{ display: 'none' }}></audio>
    </>
  );
  
}
export default AppointmentVideoCall;